import { forwardRef, useCallback, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Editor } from '@tiptap/react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import MeshMavenLoading from 'screens/common/MLCommentTextGenerationCompV1/MeshMavenLoading';
import { RephraseOptions } from 'screens/common/RephraseOptions/RephraseOptions';
import { RephraseOption } from 'screens/common/RephraseOptions/types';
import {
  useFeedbackAnalysisLoading,
  useRephraseTextLoading,
} from 'screens/performanceReview/components/ContinuousFeedback/Drawers/hooks/meshMavenDataHooks';

import { CheckCircle, ThumbsDownGenAi, ThumbsUpGenAi } from 'assets/assetsV1/iconComponents';

import { getQueryParams } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';

import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import { RichTextEditor2 } from 'uiToolkitV1/RichTextEditor2';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useCustomerFeedbackLoading, useCustomerFeedbackMutation } from '../hooks/customerFeedback';
import { useRephraseTextMutation } from '../hooks/meshMavenDataHook';
import { updateAddViolationDrawer, useSelectAddViolationDrawerData } from '../slice/addViolation.slice';

export const mixpanelEventType = {
  shorten: 'Shorten',
  elaborate: 'Elaborate',
  fix_spelling_grammar: 'Fix grammar',
  improve_writing: 'Rephrase',
};

const getUpdatedRevisions = (
  revisions: any[],
  revisionNumber: number,
  selectedRevision: any,
  editorState: Editor,
  plainText: string
) =>
  revisions.map((revision, index) => {
    if (index === revisionNumber) {
      return {
        ...revision,
        updatedState: editorState,
        isReanalyzeEnabled:
          plainText ===
          (typeof selectedRevision.updatedState === 'object'
            ? selectedRevision.updatedState.getText()
            : selectedRevision.updatedState),
      };
    }
    return revision;
  });

const RephraseEditor = forwardRef(({ rephraseLoadingText }: { rephraseLoadingText: string }, parentRef) => {
  const { rephrasedDescriptionHtml, revisionNumber, selectedRevision, revisions } = useSelectAddViolationDrawerData();

  const dispatch = useDispatch();

  const isFetching = useRephraseTextLoading();
  const isAnalysisFetching = useFeedbackAnalysisLoading();

  if (isFetching) {
    return <MeshMavenLoading loadingText={rephraseLoadingText} />;
  }

  return (
    <Box p={3}>
      {/* {rephraseError && (
        <TextLabel bold color="functional.C62C02" ml={3}>
          {rephraseError}
        </TextLabel>
      )} */}
      {/* @ts-ignore */}
      <Flex
        sx={{
          '.character-count': {
            maxWidth: '100%',
          },
          '& .rte-editor-2.ProseMirror': {
            minHeight: '110px',
          },
          ...(isMobileView()
            ? {
                '& .editor-toolbar': {
                  flexWrap: 'wrap',
                  width: '50%',
                },
              }
            : {}),
        }}
        flexDir="column"
      >
        <RichTextEditor2
          editorMaxHeight="180px"
          alwaysShowMenuBar
          placeholder="Description"
          onChange={(htmlText, plainText, editorState) => {
            dispatch(
              updateAddViolationDrawer({
                rephrasedDescription: plainText,
                rephrasedDescriptionHtml: htmlText,
                revisions: getUpdatedRevisions(revisions, revisionNumber, selectedRevision, editorState, plainText),
              })
            );
          }}
          content={rephrasedDescriptionHtml}
        />
      </Flex>
    </Box>
  );
});

const CustomerFeedback = () => {
  const { selectedRevision } = useSelectAddViolationDrawerData();
  const { mutate: submitCustomerFeedback } = useCustomerFeedbackMutation();

  const isFetching = useCustomerFeedbackLoading();
  const isRephraseFetching = useRephraseTextLoading();

  const handleFeedback = (action: 'LIKE' | 'DISLIKE') => {
    submitCustomerFeedback({ userAction: action });
  };

  if (isRephraseFetching || !selectedRevision.requestId) {
    return null;
  }

  if (selectedRevision.isAIFeedbackProvided) {
    return (
      <Flex alignItems="center" p={4}>
        <CheckCircle color="#44812F" />
        <TextBodyMedium color="#44812F" ml="4px">
          Thank you! Your response will help us improve Maven
        </TextBodyMedium>
      </Flex>
    );
  }

  return (
    <Flex
      borderTopWidth="1px"
      borderColor="neutral.EBEFF3"
      p={4}
      alignItems="center"
      bg="linear-gradient(91deg, rgba(0, 142, 240, 0.03) 1.81%, rgba(236, 59, 100, 0.03) 36.92%, rgba(255, 194, 0, 0.03) 69.52%, rgba(92, 196, 97, 0.03) 106.15%)"
    >
      <TextBodyMedium>Did you like my response?</TextBodyMedium>
      <Button
        isDisabled={isFetching}
        onClick={() => handleFeedback('LIKE')}
        StartIcon={ThumbsUpGenAi}
        variant="tertiary"
        ml="auto"
      />
      <Button
        isDisabled={isFetching}
        onClick={() => handleFeedback('DISLIKE')}
        StartIcon={ThumbsDownGenAi}
        variant="tertiary"
        ml={4}
      />
    </Flex>
  );
};

const MeshMavenRephrase = () => {
  const { mutate: rephraseText } = useRephraseTextMutation();
  const isRephraseFetching = useRephraseTextLoading();
  const isAnalysisFetching = useFeedbackAnalysisLoading();
  const parentRef = useRef(null);
  const history = useHistory();
  const [rephraseLoadingText, setRephraseLoadingText] = useState('');

  const { selectedRevision, revisionNumber, rephrasedDescription } = useSelectAddViolationDrawerData();

  const { source } = getQueryParams(history.location.search);

  const handleRephraseText = useCallback(
    (rephraseOption: RephraseOption, loadingText: string) => {
      setRephraseLoadingText(loadingText.replace('moduleName', 'disciplinary action'));
      rephraseText({ rephraseInstruction: rephraseOption });
    },
    [rephraseText]
  );

  if (!selectedRevision) {
    return null;
  }

  return (
    <Flex ref={parentRef} flexDir="column" borderRadius="8px" borderWidth="1px" borderColor="neutral.EBEFF3">
      <Flex p="10px 20px 6px" alignItems="center" justifyContent="space-between">
        <TextBodyMedium bold>Description</TextBodyMedium>

        <RephraseOptions
          handleRephraseText={handleRephraseText}
          isDisabled={isRephraseFetching || isAnalysisFetching}
          totalCharacters={rephrasedDescription.length}
          tooltipText=""
        />
      </Flex>
      <Divider color="neutral.EBEFF3" m={1} />
      <RephraseEditor key={revisionNumber} ref={parentRef} rephraseLoadingText={rephraseLoadingText} />
      <CustomerFeedback />
    </Flex>
  );
};

export { MeshMavenRephrase };
